/**
 * @fileoverview WEB-714 — `GenericModule`: the shared renderer for the
 * `generic` and `agenda` module kinds (`lib/eventDetail/modules.ts`).
 *
 * ## The restraint this module exists to prove
 *
 * A category earns a bespoke module-tier component when its data model is
 * genuinely different, not merely because it is a different category. Four
 * of the ten category slugs order only the `generic` kind
 * (`config/eventLexicon.ts` → `genericModuleOrder`), and a fifth orders the
 * `agenda` kind for the same time-led shape (WEB-749 design spec §5.2). All
 * five lexicon entries render through this one component; the only
 * difference between them is the wording already resolved onto
 * `module.title` / `module.meta` before this file ever runs. This file must
 * therefore contain **no category-slug string literal** —
 * `__tests__/GenericModule.test.tsx` grep-asserts that directly against this
 * file's source.
 *
 * ## Why it ships fully degraded today
 *
 * None of the five lexicon entries that route through this renderer has a
 * structured backing column on `EventDetailV2` today (WEB-749 design spec
 * §5.2, "Degrade case": no pour/agenda-item/checklist rows exist on the
 * wire). {@link resolveGenericModuleRows} states that once, unconditionally
 * returning an empty array — `resolveEventModules` folds that emptiness
 * into `hasContent` (WEB-756) so WEB-719 can paint the degrade note.
 * Wiring a real source later is a change inside that one function, never a
 * template or DOM change here.
 *
 * ## Row shape
 *
 * `ModuleRowList` (WEB-707) already self-gates every optional row part: a
 * row with a `lead` renders the time / numeral column, a row without simply
 * does not. That is what lets one renderer cover both the time-led shape
 * (§5.2) and the flat list shape (§5.1) with zero branching here — the
 * caller (this file) never inspects which parts a row happens to carry.
 */
import type { ReactElement } from 'react';

import {
  EventModuleSection,
  ModuleRowList,
  type ModuleRow,
} from '@/components/organisms/EventModuleSection';
import { resolveGenericModuleRows } from '@/lib/eventDetail/genericModuleRows';

import type { EventModuleProps } from './registry';

export { resolveGenericModuleRows } from '@/lib/eventDetail/genericModuleRows';

export interface GenericModuleProps extends EventModuleProps {
  /**
   * Row-source override. Test-only seam: production call sites (the
   * registry) never pass this, so they always get
   * `resolveGenericModuleRows(event)` — unconditionally empty today. Without
   * this seam, a test asserting "every lexicon entry produces the same DOM
   * shape" could only ever observe an empty module, which would pass
   * whether or not this component's row-to-DOM mapping is even correct.
   */
  rows?: readonly ModuleRow[];
}

/**
 * `GenericModule` — a `ModuleRowList` with no visual, driven entirely by the
 * lexicon's heading (`module.title`) and the resolved rows. `module.meta`
 * and `module.moreHref` ride through unchanged to `EventModuleSection`,
 * which already self-gates them; this file makes no separate decision about
 * either.
 *
 * `module.hasContent` is authoritative (`resolveEventModules`, WEB-700 /
 * WEB-756) and is never re-derived here. Row emptiness is folded into that
 * flag via {@link resolveGenericModuleRows}; a second length check here is
 * how the two drift apart (WEB-719 would then paint a degrade note over a
 * renderer that returned `null`).
 */
export function GenericModule({
  module,
  event,
  chroma,
  rows,
}: GenericModuleProps): ReactElement | null {
  if (!module.hasContent) {
    return null;
  }

  const resolvedRows = rows ?? resolveGenericModuleRows(event);

  return (
    <EventModuleSection module={module} chroma={chroma}>
      <ModuleRowList rows={resolvedRows} chroma={chroma} />
    </EventModuleSection>
  );
}
